// Developer: Ankit Gupta
/**
 * English or Arabic, for the whole product, carried in every shell's top bar.
 *
 * One button that switches, like the colour mode beside it. Its face is the
 * other language written in that language ("العربية" on an English page,
 * "English" on an Arabic one): a reader who cannot read the current language
 * must still be able to find theirs. The choice is remembered on this browser
 * (i18n/setLanguage), on the account, so e-mails, exports and Orion's
 * replies written later, away from this page, use it too, and on the sign-in
 * account, so the next sign-in page opens in it.
 */
import { Tooltip } from 'antd';
import { TranslationOutlined } from '@ant-design/icons';
import { setLanguage, useLanguage, useT, type Language } from '@/i18n';
import { apiClient, keycloakInstance } from '@/api/client';
import { saveOnAccount } from '@/auth/signInLanguage';

const OTHER: Record<Language, Language> = { en: 'ar', ar: 'en' };
const NAME: Record<Language, string> = { en: 'English', ar: 'العربية' };

export function LanguageToggle() {
  const { t } = useT('common');
  const language = useLanguage();
  const next = OTHER[language];

  const choose = () => {
    void setLanguage(next);
    // Signed out (the public page) the browser alone remembers it, and the sign-in
    // page is opened in it from there.
    const kc = keycloakInstance();
    if (!kc?.authenticated) return;
    // Best effort: the account remembers it for what is written away from the
    // page. A failure leaves the page switched and changes nothing else.
    apiClient.put('/profile/me', { language: next }).catch(() => undefined);
    // and the sign-in account, so the next sign-in page opens in it (auth/signInLanguage).
    saveOnAccount(kc, next).catch(() => undefined);
  };

  return (
    <Tooltip title={t('language.switchTo', { language: NAME[next] })}>
      <button
        type="button"
        className="dp-icon-button dp-language-toggle"
        onClick={choose}
        aria-label={t('language.switchTo', { language: NAME[next] })}
        lang={next}
      >
        <TranslationOutlined />
        <span className="dp-language-toggle-name">{NAME[next]}</span>
      </button>
    </Tooltip>
  );
}

export default LanguageToggle;
